import {LastQuoteCacheService} from "../last-quote-cache.service";
import {QuoteDto} from "../shell-communication/dtos/quote-dto.class";
import {ExpectedMoveSettings} from "./expected-move.service";
import {getStrikeStep, isValidNumber} from "../utils";
import {MessageBusService} from "../message-bus.service";
import {isRangeModelResolvable} from "./expected-move-range-model";

export interface ExpectedMoveBand {
    rangeStart: number;
    rangeEnd: number;
    oneWayValue: number;
}

export class ExpectedMoveRangeProcessor {

    constructor(
        private readonly _expirationTicker: string,
        private readonly _underlyingTicker: string,
        private readonly _expiration: string,
        private _settings: ExpectedMoveSettings,
        private readonly _lastQuoteCache: LastQuoteCacheService,
        private readonly _messageBus: MessageBusService
    ) {
    }

    private _atm: number;
    private _callTicker: string;
    private _putTicker: string;
    private _adjCallLowerTicker: string;
    private _adjCallUpperTicker: string;
    private _adjPutLowerTicker: string;
    private _adjPutUpperTicker: string;

    private _callPx: number;
    private _putPx: number;

    get id(): string {
        return `${this._underlyingTicker}:${this._expiration}`;
    }

    get ticker(): string {
        return this._underlyingTicker;
    }

    get expiration(): string {
        return this._expiration;
    }

    get callTicker(): string {
        return this._callTicker;
    }

    get putTicker(): string {
        return this._putTicker;
    }

    matchesTicker(ticker: string): boolean {
        return ticker === this._callTicker || ticker === this._putTicker
            || ticker === this._adjCallLowerTicker || ticker === this._adjCallUpperTicker
            || ticker === this._adjPutLowerTicker || ticker === this._adjPutUpperTicker;
    }

    private getAllTickers(): string[] {
        return [
            this._callTicker, this._putTicker,
            this._adjCallLowerTicker, this._adjCallUpperTicker,
            this._adjPutLowerTicker, this._adjPutUpperTicker
        ].filter(t => !!t);
    }

    rangeStart: number;
    rangeEnd: number;
    oneWayValue: number;
    atmStraddleValue: number;
    private _secondBand: ExpectedMoveBand;

    onAtmChanged(currentAtm?: number) {

        if (!isValidNumber(currentAtm)) {
            this._lastQuoteCache.unsubscribeTickers(this.getAllTickers());
            this._callTicker = this._putTicker = undefined;
            this._adjCallLowerTicker = this._adjCallUpperTicker = undefined;
            this._adjPutLowerTicker = this._adjPutUpperTicker = undefined;
            this._callPx = this._putPx = undefined;
            this._atm = undefined;
            this.calculateRange();
            return;
        }

        this._atm = currentAtm;

        const step = getStrikeStep(this._underlyingTicker);
        const strikeLower = currentAtm - step;
        const strikeUpper = currentAtm + step;

        const callTicker = `${this._expirationTicker} ${this._underlyingTicker} Call ${currentAtm} ${this._expiration}`;
        const putTicker = `${this._expirationTicker} ${this._underlyingTicker} Put ${currentAtm} ${this._expiration}`;

        const adjCallLowerTicker = `${this._expirationTicker} ${this._underlyingTicker} Call ${strikeLower} ${this._expiration}`;
        const adjCallUpperTicker = `${this._expirationTicker} ${this._underlyingTicker} Call ${strikeUpper} ${this._expiration}`;
        const adjPutLowerTicker = `${this._expirationTicker} ${this._underlyingTicker} Put ${strikeLower} ${this._expiration}`;
        const adjPutUpperTicker = `${this._expirationTicker} ${this._underlyingTicker} Put ${strikeUpper} ${this._expiration}`;

        this._lastQuoteCache.unsubscribeTickers(this.getAllTickers());

        this._callTicker = callTicker;
        this._putTicker = putTicker;
        this._adjCallLowerTicker = adjCallLowerTicker;
        this._adjCallUpperTicker = adjCallUpperTicker;
        this._adjPutLowerTicker = adjPutLowerTicker;
        this._adjPutUpperTicker = adjPutUpperTicker;

        this._callPx = undefined;
        this._putPx = undefined;

        this._lastQuoteCache.subscribeTickers(this.getAllTickers());

        this.calculateRange();
    }

    onQuote(quoteDto: QuoteDto) {
        if (this._callTicker === quoteDto.ticker) {
            this._callPx = quoteDto.mid;
        } else if (this._putTicker === quoteDto.ticker) {
            this._putPx = quoteDto.mid;
        }
        // Adjacent quotes are not stored — calculateRange() reads them from cache

        this.calculateRange();
    }

    isStrikeInRange(strike: number) {
        if (!isValidNumber(strike)) {
            return false;
        }

        if (!isValidNumber(this.rangeStart) || !isValidNumber(this.rangeEnd)) {
            return false;
        }

        return strike >= this.rangeStart && strike <= this.rangeEnd;
    }

    getDisplayBands(): ExpectedMoveBand[] {
        const bands: ExpectedMoveBand[] = [];
        if (this._settings.rangeModel !== 'Custom Percentage' || this._settings.range1Enabled !== false) {
            if (isValidNumber(this.oneWayValue)) {
                bands.push({rangeStart: this.rangeStart, rangeEnd: this.rangeEnd, oneWayValue: this.oneWayValue});
            }
        }
        if (this._settings.rangeModel === 'Custom Percentage' && this._settings.range2Enabled && this._secondBand) {
            bands.push(this._secondBand);
        }
        return bands.sort((a, b) => (a.rangeEnd - a.rangeStart) - (b.rangeEnd - b.rangeStart)
            || a.oneWayValue - b.oneWayValue);
    }

    private makeBand(sum: number, scale: number): ExpectedMoveBand {
        const distance = sum * scale;
        if (!isValidNumber(distance) || distance < 0) { return undefined; }
        const step = getStrikeStep(this._underlyingTicker);
        return {
            oneWayValue: Math.round(distance),
            rangeStart: Math.floor((this._atm - distance) / step) * step,
            rangeEnd: Math.ceil((this._atm + distance) / step) * step
        };
    }

    calculateRange() {
        this.rangeStart = this.rangeEnd = this.oneWayValue = this.atmStraddleValue = undefined;
        this._secondBand = undefined;

        try {
            if (!isValidNumber(this._atm)) { return; }
            this._callPx = this._lastQuoteCache.getLastQuote(this._callTicker)?.mid;
            this._putPx = this._lastQuoteCache.getLastQuote(this._putTicker)?.mid;

            // Interpolate from adjacent strikes if ATM prices are missing
            if (!isValidNumber(this._callPx) || !isValidNumber(this._putPx)) {
                if (!isValidNumber(this._callPx)) {
                    const lowerMid = this._lastQuoteCache.getLastQuote(this._adjCallLowerTicker)?.mid;
                    const upperMid = this._lastQuoteCache.getLastQuote(this._adjCallUpperTicker)?.mid;
                    if (isValidNumber(lowerMid) && isValidNumber(upperMid)) {
                        this._callPx = (lowerMid + upperMid) / 2;
                    }
                }
                if (!isValidNumber(this._putPx)) {
                    const lowerMid = this._lastQuoteCache.getLastQuote(this._adjPutLowerTicker)?.mid;
                    const upperMid = this._lastQuoteCache.getLastQuote(this._adjPutUpperTicker)?.mid;
                    if (isValidNumber(lowerMid) && isValidNumber(upperMid)) {
                        this._putPx = (lowerMid + upperMid) / 2;
                    }
                }
            }

            const sum = this._callPx + this._putPx;

            if (!isValidNumber(sum) || sum < 0) {
                return;
            }

            // Both percentages multiply this same, unrounded sum. Keep the primary scalar/band
            // independent of overlay switches for existing consumers.
            this.atmStraddleValue = sum;
            if (isRangeModelResolvable(this._settings)) {
                const band = this.makeBand(sum, this._settings.rangeModel === 'ATM Straddle'
                    ? 1 : this._settings.rangeModelCustomPercent);
                if (band) {
                    this.rangeStart = band.rangeStart;
                    this.rangeEnd = band.rangeEnd;
                    this.oneWayValue = band.oneWayValue;
                }
            }
            if (this._settings.rangeModel === 'Custom Percentage'
                && isRangeModelResolvable({...this._settings, rangeModelCustomPercent: this._settings.range2CustomPercent})) {
                this._secondBand = this.makeBand(sum, this._settings.range2CustomPercent);
            }

        } finally {
            this._messageBus.publishAsync({
                topic: 'ExpectedMoveRangeChanged',
                payload: {
                    ticker: this.ticker,
                    expiration: this.expiration
                }
            });
        }
    }

    onSettingsChanged(expectedMoveSettings: ExpectedMoveSettings) {
        this._settings = expectedMoveSettings;
        this.calculateRange();
    }
}
